<template>
  <!-- Backdrop click only closes on the FIRST step. Past that the user has an
       uploaded file, a classification and possibly an edited schema draft in
       here; a stray click (or a text-selection drag that happens to end on the
       backdrop) was discarding all of it with no confirmation. Use the × . -->
  <div class="fixed inset-0 z-40 bg-black/30 flex items-center justify-center" @click.self="onBackdropClick">
    <div class="bg-white rounded-lg border border-gray-200 w-[64rem] max-w-[95vw] max-h-[88vh] flex flex-col">
      <!-- Header + steps -->
      <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-4">
          <h3 class="text-sm font-semibold text-gray-900">Discover a file</h3>
          <div class="flex items-center gap-1 text-xs">
            <template v-for="(label, i) in ['File', 'Sheets', 'Classify', 'Review']" :key="label">
              <span v-if="i" class="text-gray-300 mx-1">›</span>
              <span
                :class="step === i ? 'text-primary-500 font-medium' : (step > i ? 'text-gray-600' : 'text-gray-300')"
              >{{ label }}</span>
            </template>
          </div>
        </div>
        <button class="text-gray-400 hover:text-gray-600" @click="tryClose">×</button>
      </div>

      <!-- Body -->
      <div class="p-4 overflow-y-auto flex-1">
        <!-- STEP 0: pick or upload a file -->
        <div v-if="step === 0">
          <div class="flex gap-2 mb-4">
            <button
              class="text-xs px-2.5 py-1 rounded border"
              :class="fileMode === 'upload' ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600'"
              @click="fileMode = 'upload'"
            >
              Upload new
            </button>
            <button
              class="text-xs px-2.5 py-1 rounded border"
              :class="fileMode === 'browse' ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600'"
              @click="switchToBrowse"
            >
              Pick from S3
            </button>
          </div>

          <div v-if="fileMode === 'upload'">
            <label
              class="block rounded-lg border-2 border-dashed border-gray-200 p-8 text-center text-sm text-gray-500 cursor-pointer hover:border-gray-400"
            >
              <template v-if="uploading">Uploading… {{ uploadProgress }}%</template>
              <template v-else-if="s3Key">
                <span class="text-gray-800 font-mono">{{ s3Key }}</span>
                <span class="block text-xs text-gray-400 mt-1">Click to replace</span>
              </template>
              <template v-else>Click to choose a file (.csv .xlsx .xls .xlsm .xlsb .zip .txt)</template>
              <input type="file" class="hidden" accept=".csv,.xlsx,.xls,.xlsm,.xlsb,.zip,.txt" @change="uploadFile" />
            </label>
          </div>

          <div v-else>
            <div class="flex items-center gap-2 mb-2">
              <input
                v-model.trim="browsePrefix"
                type="text"
                class="border border-gray-200 rounded px-2 py-1 text-xs font-mono w-64 focus:outline-none focus:border-gray-400"
                @keyup.enter="listFiles"
              />
              <button class="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:border-gray-400" @click="listFiles">
                List
              </button>
              <span v-if="browseLoading" class="text-xs text-gray-400">Loading…</span>
            </div>
            <div class="border border-gray-100 rounded max-h-72 overflow-y-auto">
              <button
                v-for="file in browseFiles"
                :key="file.key"
                class="w-full flex items-center justify-between px-3 py-1.5 text-xs hover:bg-gray-50 border-b border-gray-50 last:border-0 text-left"
                :class="{ 'bg-primary-50': s3Key === file.key }"
                @click="s3Key = file.key"
              >
                <span class="font-mono text-gray-700 break-all">{{ file.relative_path }}</span>
                <span class="text-gray-400 shrink-0 ml-3">{{ formatSize(file.size) }} · {{ formatDate(file.last_modified) }}</span>
              </button>
              <p v-if="!browseLoading && !browseFiles.length" class="px-3 py-4 text-xs text-gray-400 text-center">
                No files under this prefix.
              </p>
            </div>
          </div>

          <p v-if="stepError" class="text-xs text-red-600 mt-3">{{ stepError }}</p>
        </div>

        <!-- STEP 1: select sheets -->
        <div v-else-if="step === 1">
          <p class="text-xs text-gray-500 mb-2">
            {{ analysis.total_files }} file{{ analysis.total_files === 1 ? '' : 's' }},
            {{ analysis.total_sheets }} sheet{{ analysis.total_sheets === 1 ? '' : 's' }} —
            select the sheets to classify.
          </p>
          <div v-for="file in analysis.file_sheets" :key="fileNameOf(file)" class="mb-3">
            <p class="text-xs font-medium text-gray-700 font-mono mb-1">{{ fileNameOf(file) }}</p>
            <div class="flex flex-wrap gap-1.5">
              <label
                v-for="sheet in sheetsOf(file)"
                :key="sheetKey(file, sheet)"
                class="inline-flex items-center gap-1.5 text-xs border rounded px-2 py-1 cursor-pointer"
                :class="selectedSheets.has(sheetKey(file, sheet))
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-400'"
              >
                <input
                  type="checkbox"
                  class="hidden"
                  :checked="selectedSheets.has(sheetKey(file, sheet))"
                  @change="toggleSheet(file, sheet)"
                />
                {{ sheetNameOf(sheet) }}
              </label>
            </div>
          </div>
          <p v-if="stepError" class="text-xs text-red-600 mt-2">{{ stepError }}</p>
        </div>

        <!-- STEP 2: classify -->
        <div v-else-if="step === 2" class="py-10 text-center">
          <p class="text-sm text-gray-700 mb-2">Classifying {{ progress.total }} sheet{{ progress.total === 1 ? '' : 's' }}…</p>
          <div class="w-72 h-2 bg-gray-100 rounded-full mx-auto overflow-hidden mb-2">
            <div
              class="h-full bg-primary-500 rounded-full transition-all"
              :style="{ width: progress.total ? (progress.current / progress.total) * 100 + '%' : '0%' }"
            ></div>
          </div>
          <p class="text-xs text-gray-400">{{ progress.message || `${progress.current}/${progress.total}` }}</p>
          <p v-if="stepError" class="text-xs text-red-600 mt-3">{{ stepError }}</p>
        </div>

        <!-- STEP 3: review results -->
        <div v-else-if="step === 3">
          <table class="w-full text-xs">
            <thead>
              <tr class="text-left text-gray-500 border-b border-gray-100">
                <th class="py-1.5 pr-3 font-medium">Sheet</th>
                <th class="py-1.5 pr-3 font-medium">Type</th>
                <th class="py-1.5 pr-3 font-medium">Conf.</th>
                <th class="py-1.5 pr-3 font-medium">Suggested table</th>
                <th class="py-1.5 pr-3 font-medium">Schema match</th>
                <th class="py-1.5 pr-3 font-medium">Parsed</th>
                <th class="py-1.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(result, idx) in results" :key="idx">
                <tr class="border-b border-gray-50 hover:bg-gray-50">
                  <td class="py-1.5 pr-3">
                    <span class="text-gray-800">{{ result.sheet_name }}</span>
                    <span class="block text-gray-400 break-all">{{ result.file_name }}</span>
                  </td>
                  <td class="py-1.5 pr-3">
                    <span class="px-1.5 py-0.5 rounded" :class="sheetTypeClass(result.sheet_type)">
                      {{ sheetTypeLabel(result.sheet_type) }}
                    </span>
                  </td>
                  <td class="py-1.5 pr-3 font-mono" :class="confidenceClass(result.confidence)">
                    {{ Math.round((result.confidence || 0) * 100) }}%
                  </td>
                  <td class="py-1.5 pr-3 font-mono text-gray-600 break-all">{{ result.suggested_table_name }}</td>
                  <td class="py-1.5 pr-3">
                    <template v-if="result.schema_match && result.schema_match.found">
                      <span class="font-mono text-gray-700">{{ result.schema_match.table_name }}</span>
                      <span class="text-gray-400">
                        {{ result.schema_match.match_type === 'exact' ? ' (exact)' : ` (${Math.round((result.schema_match.confidence || 0) * 100)}%)` }}
                      </span>
                    </template>
                    <span v-else class="text-gray-400">none</span>
                  </td>
                  <td class="py-1.5 pr-3">
                    <span v-if="result.parse_history && result.parse_history.parsed" class="text-purple-600">
                      {{ formatDate(result.parse_history.last_parse_date) }}
                    </span>
                    <span v-else class="text-gray-400">no</span>
                  </td>
                  <td class="py-1.5 text-right whitespace-nowrap">
                    <button
                      v-if="result.schema_match && result.schema_match.found"
                      class="text-xs px-2 py-0.5 rounded border border-gray-200 text-gray-600 hover:border-gray-400 mr-1.5 disabled:opacity-50"
                      :disabled="loadingKeys.has(resultKey(result))"
                      @click="loadToExisting(result)"
                    >
                      {{ loadingKeys.has(resultKey(result)) ? 'Loading…' : 'Load data' }}
                    </button>
                    <button
                      class="text-xs px-2 py-0.5 rounded bg-primary-500 text-white hover:bg-primary-600"
                      @click="openSave(idx)"
                    >
                      {{ saveIdx === idx ? 'Close' : 'New schema' }}
                    </button>
                  </td>
                </tr>
                <tr v-if="saveIdx === idx" class="border-b border-gray-50">
                  <td colspan="7" class="py-3 px-3 bg-gray-50/60">
                    <SchemaForm v-model="saveDraft" editable is-new class="mb-3" />
                    <p v-if="!hasFilters" class="text-xs text-amber-600 mb-2">
                      No filename filters — this schema won't auto-parse future files. Add one unless this is a one-off load.
                    </p>
                    <div class="flex items-center gap-3">
                      <label class="inline-flex items-center gap-1.5 text-xs text-gray-700">
                        <input v-model="saveLoadData" type="checkbox" />
                        Load this sheet's data after saving
                      </label>
                      <button
                        class="ml-auto text-xs px-2.5 py-1 rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
                        :disabled="savingSchema"
                        @click="saveSchema(result)"
                      >
                        {{ savingSchema ? 'Saving…' : 'Save schema' }}
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <p v-if="stepError" class="text-xs text-red-600 mt-2">{{ stepError }}</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-4 py-3 border-t border-gray-100 flex items-center justify-between shrink-0">
        <button
          v-if="step > 0 && step !== 2"
          class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:border-gray-400"
          @click="step = step === 3 ? 1 : step - 1"
        >
          Back
        </button>
        <span v-else></span>
        <div class="flex items-center gap-2">
          <button
            v-if="step === 0"
            class="text-xs px-2.5 py-1 rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
            :disabled="!s3Key || analyzing || uploading"
            @click="analyze"
          >
            {{ analyzing ? 'Analyzing…' : 'Analyze' }}
          </button>
          <button
            v-if="step === 1"
            class="text-xs px-2.5 py-1 rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
            :disabled="!selectedSheets.size"
            @click="classify"
          >
            Classify {{ selectedSheets.size }} sheet{{ selectedSheets.size === 1 ? '' : 's' }}
          </button>
          <button
            v-if="step === 3"
            class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:border-gray-400"
            @click="$emit('close')"
          >
            Done
          </button>
        </div>
      </div>

      <!-- Already-parsed confirmation -->
      <div
        v-if="alreadyParsed"
        class="absolute inset-0 bg-black/20 flex items-center justify-center rounded-lg"
        @click.self="alreadyParsed = null"
      >
        <div class="bg-white rounded-lg border border-gray-200 w-96 p-4">
          <h4 class="text-sm font-semibold text-gray-900 mb-2">Already parsed</h4>
          <p class="text-xs text-gray-600 mb-3">
            This sheet was parsed {{ formatDate(alreadyParsed.result.parse_history.last_parse_date) }}
            ({{ formatCount(alreadyParsed.result.parse_history.rows_loaded) }} rows). Loading again may
            append duplicate rows.
          </p>
          <div class="flex items-center justify-end gap-2">
            <button class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600" @click="alreadyParsed = null">
              Cancel
            </button>
            <button class="text-xs px-2.5 py-1 rounded bg-amber-500 text-white hover:bg-amber-600" @click="confirmLoad">
              Load anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {
  formatDate, formatSize, formatCount, sheetTypeClass, sheetTypeLabel, confidenceClass,
  etlErrorMessage,
} from '@/utils/etlFormat';
import SchemaForm from './SchemaForm.vue';

const SHEET_SEP = '\u0000'; // never appears in a sheet name, unlike '::'

export default {
  name: 'DiscoverWizard',
  components: { SchemaForm },
  emits: ['close', 'schema-created', 'toast'],
  data() {
    return {
      step: 0,
      stepError: '',
      // step 0
      fileMode: 'upload',
      uploading: false,
      uploadProgress: 0,
      s3Key: null,
      browsePrefix: 'uploads',
      browseFiles: [],
      browseLoading: false,
      browseLoaded: false,
      // step 1
      analysis: null,
      analyzing: false,
      selectedSheets: new Set(),
      // step 2
      progress: { current: 0, total: 0, message: '' },
      cancelled: false,
      // step 3
      results: [],
      saveIdx: null,
      saveDraft: null,
      saveLoadData: true,
      savingSchema: false,
      loadingKeys: new Set(),
      alreadyParsed: null,
    };
  },
  computed: {
    hasFilters() {
      return (this.saveDraft?.filename_filter?.filters || []).length > 0;
    },
  },
  beforeUnmount() {
    this.cancelled = true;
  },
  methods: {
    formatDate,
    formatSize,
    formatCount,
    sheetTypeClass,
    sheetTypeLabel,
    confidenceClass,
    tryClose() {
      this.cancelled = true;
      this.$emit('close');
    },
    onBackdropClick() {
      // Nothing to lose on step 0; anywhere else the click is almost always
      // accidental, so ignore it rather than throw the session away.
      if (this.step === 0 && !this.s3Key) this.tryClose();
    },

    // ---- step 0 ----
    switchToBrowse() {
      this.fileMode = 'browse';
      if (!this.browseLoaded) this.listFiles();
    },
    async uploadFile(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;
      this.uploading = true;
      this.uploadProgress = 0;
      this.stepError = '';
      try {
        const { data: presigned } = await axios.post('/api/etl_module/upload/', {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
        });
        // Clean instance: the default axios interceptor appends tenant_slug to
        // every request, which corrupts a presigned S3 URL's signature.
        const put = await fetch(presigned.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!put.ok) {
          throw new Error(`S3 upload failed (${put.status})`);
        }
        this.s3Key = presigned.s3_key;
      } catch (err) {
        this.stepError = etlErrorMessage(err, 'Upload failed.');
      }
      this.uploading = false;
    },
    async listFiles() {
      this.browseLoading = true;
      this.stepError = '';
      try {
        const { data } = await axios.get('/api/etl_module/files/', {
          params: { prefix: this.browsePrefix },
        });
        this.browseFiles = data.files;
        this.browseLoaded = true;
      } catch (err) {
        this.stepError = etlErrorMessage(err, 'Failed to list files.');
      }
      this.browseLoading = false;
    },
    async analyze() {
      this.analyzing = true;
      this.stepError = '';
      try {
        const { data } = await axios.post('/api/etl_module/analyze/', { s3_key: this.s3Key });
        this.analysis = data;
        this.selectedSheets = new Set();
        this.step = 1;
      } catch (err) {
        this.stepError = etlErrorMessage(err, 'Failed to analyze file.');
      }
      this.analyzing = false;
    },

    // ---- step 1 ----
    fileNameOf(file) {
      return file.file_name || file.name || file.s3_key;
    },
    sheetsOf(file) {
      return (file.sheets || file.sheet_names || []).map((s) => (typeof s === 'string' ? { sheet_name: s } : s));
    },
    sheetNameOf(sheet) {
      return sheet.sheet_name || sheet.name;
    },
    sheetKey(file, sheet) {
      return `${this.fileNameOf(file)}${SHEET_SEP}${this.sheetNameOf(sheet)}`;
    },
    toggleSheet(file, sheet) {
      const key = this.sheetKey(file, sheet);
      const next = new Set(this.selectedSheets);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      this.selectedSheets = next;
    },
    buildSheetPayload() {
      const payload = [];
      for (const file of this.analysis.file_sheets) {
        for (const sheet of this.sheetsOf(file)) {
          if (!this.selectedSheets.has(this.sheetKey(file, sheet))) continue;
          payload.push({
            sheet_name: this.sheetNameOf(sheet),
            file_name: this.fileNameOf(file),
            s3_key: file.s3_key || this.s3Key,
            from_zip: !!(file.from_zip || this.analysis.is_zip),
            zip_s3_key: file.zip_s3_key || this.analysis.original_zip || null,
          });
        }
      }
      return payload;
    },

    // ---- step 2 ----
    async classify() {
      const sheets = this.buildSheetPayload();
      this.step = 2;
      this.stepError = '';
      this.cancelled = false;
      this.progress = { current: 0, total: sheets.length, message: '' };
      try {
        const { data: job } = await axios.post('/api/etl_module/classify-sheets/', { sheets });
        const results = await this.pollJob(job.job_id);
        if (this.cancelled) return;
        this.results = results;
        await this.mergeParseHistory(sheets);
        this.step = 3;
      } catch (err) {
        if (!this.cancelled) {
          this.stepError = etlErrorMessage(err, 'Classification failed.');
        }
      }
    },
    async pollJob(jobId) {
      const started = Date.now();
      while (!this.cancelled) {
        const { data } = await axios.get(`/api/jobs/${jobId}/status/`);
        this.progress = {
          current: data.progress_current || 0,
          total: data.progress_total || this.progress.total,
          message: data.progress_message || '',
        };
        if (data.status === 'completed') return data.result_data.classifications;
        if (data.status === 'failed') throw new Error(data.error_message || 'Classification failed');
        if (Date.now() - started > 10 * 60 * 1000) throw new Error('Classification timed out after 10 minutes');
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      return [];
    },
    async mergeParseHistory(sheets) {
      // Batches of 25 keep the JSON-in-query-param under URL length limits.
      try {
        const byKey = {};
        for (let i = 0; i < sheets.length; i += 25) {
          const batch = sheets.slice(i, i + 25).map((s) => ({ file_name: s.file_name, sheet_name: s.sheet_name }));
          const { data } = await axios.get('/api/etl_module/parse-history/check/', {
            params: { sheets: JSON.stringify(batch) },
          });
          Object.assign(byKey, data.results || data);
        }
        for (const result of this.results) {
          const info = byKey[`${result.file_name}::${result.sheet_name}`];
          if (info) result.parse_history = info;
        }
      } catch {
        // History check is advisory; a failure must not block review — but say so.
        this.$emit('toast', {
          message: 'Could not check parse history — "Parsed" column may be incomplete.',
          kind: 'error',
        });
      }
    },

    // ---- step 3 ----
    resultKey(result) {
      return `${result.file_name}${SHEET_SEP}${result.sheet_name}`;
    },
    openSave(idx) {
      if (this.saveIdx === idx) {
        this.saveIdx = null;
        return;
      }
      const result = this.results[idx];
      const columns = (result.columns || []).map((col, i) => ({
        name: (typeof col === 'string' ? col : col.name) || `COLUMN_${i}`,
        snowflake_type: 'VARCHAR',
        nullable: true,
      }));
      this.saveDraft = {
        table_name: result.suggested_table_name || '',
        snowflake_schema: 'SOURCE_DATA_PRIVATE_PAYER',
        sheet_type: result.sheet_type || 'data_sheet',
        description: '',
        dont_parse: false,
        columns,
        primary_key_columns: [],
        filename_filter: null,
        target_sheet_names: [],
      };
      this.saveLoadData = true;
      this.saveIdx = idx;
    },
    async saveSchema(result) {
      this.savingSchema = true;
      this.stepError = '';
      try {
        const { data } = await axios.post('/api/etl_module/schemas/from-classification/', {
          classification: result,
          table_name: this.saveDraft.table_name,
          snowflake_schema: this.saveDraft.snowflake_schema,
          sheet_type: this.saveDraft.sheet_type,
          description: this.saveDraft.description,
          dont_parse: this.saveDraft.dont_parse,
          primary_key_columns: this.saveDraft.primary_key_columns,
          filename_filter: this.saveDraft.filename_filter,
          target_sheet_names: this.saveDraft.target_sheet_names,
          load_data: this.saveLoadData,
        });
        // A 201 can still carry a failed data load — report it honestly.
        if (this.saveLoadData && data.data_loaded === false) {
          this.$emit('toast', {
            message: `Schema ${data.table_name} saved, but the data load FAILED: ${data.load_error || 'unknown error'}`,
            kind: 'error',
          });
        } else {
          this.$emit('toast', { message: data.message || 'Schema saved', kind: 'success' });
        }
        this.saveIdx = null;
        this.$emit('schema-created', data);
      } catch (err) {
        const detail = err?.response?.data;
        // Serializer errors arrive as {field: [msg]} and have no detail/error
        // key, so keep the field-flattening fallback below etlErrorMessage.
        this.stepError = detail?.detail || detail?.error
          || (typeof detail === 'object' && detail !== null
            ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join('; ')
            : etlErrorMessage(err, 'Failed to save schema.'));
      }
      this.savingSchema = false;
    },
    loadToExisting(result) {
      if (result.parse_history && result.parse_history.parsed) {
        this.alreadyParsed = { result };
        return;
      }
      this.doLoad(result);
    },
    confirmLoad() {
      const { result } = this.alreadyParsed;
      this.alreadyParsed = null;
      this.doLoad(result);
    },
    async doLoad(result) {
      const key = this.resultKey(result);
      this.loadingKeys = new Set([...this.loadingKeys, key]);
      try {
        const { data } = await axios.post('/api/etl_module/load-sheet-data/', {
          schema_id: result.schema_match.schema_id,
          classification: result,
        });
        result.parse_history = {
          parsed: true,
          last_parse_date: new Date().toISOString(),
          rows_loaded: data.rows_loaded,
        };
        this.$emit('toast', {
          message: `Loaded ${formatCount(data.rows_loaded)} rows to ${data.table_name}`,
          kind: 'success',
        });
      } catch (err) {
        this.$emit('toast', {
          message: etlErrorMessage(err, `Failed to load ${result.sheet_name}`),
          kind: 'error',
        });
      }
      const next = new Set(this.loadingKeys);
      next.delete(key);
      this.loadingKeys = next;
    },
  },
};
</script>
